/*
 * Created Date: Thursday October 6th 2022
 * Author: Oskar Karnblad
 * License: Proprietary to Scaffcalc AB
 *
 * Copyright (c) 2022 Scaffcalc AB
 */
import { InfoOutlined } from "@mui/icons-material";
import {
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import { FC } from "react";
import ToolTip from "./ToolTip";
import { useTranslation } from "react-i18next";

export interface TagDropDownInterface {
  infoButton?: boolean;
  onChange?: (event: SelectChangeEvent<(string | number)[]>) => void;
  onClickInfo?: any;

  options: { label: string; value: string | number; disabled?: boolean }[];
  placeholder: string;
  selectedOptions: (string | number)[];
  icon?: any;
  style?: any;
  disabled?: boolean;
  error?: boolean;
  required?: any;
  helperText?: string;
  backgroundColor?: string;
  suffix?: string;
  tippyText?: string;
}

const TagDropDown: FC<TagDropDownInterface> = ({
  infoButton,
  icon,
  onChange,
  onClickInfo,
  options,
  placeholder,
  selectedOptions,
  style,
  disabled,
  required,
  error,
  helperText,
  backgroundColor,
  suffix,
  tippyText,
}) => {
  const { t } = useTranslation();

  return (
    <Grid
      container
      sx={{
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      {icon ? (
        <Grid item sx={{ display: "flex", marginRight: "4px" }}>
          {icon}
        </Grid>
      ) : null}
      <Grid item sx={{ flex: 1, display: "flex", width: "100%" }}>
        <FormControl
          disabled={disabled}
          fullWidth
          margin="dense"
          size="small"
          required={required}
          error={error}
          sx={{
            "& .MuiFormLabel-root.Mui-focused ": {
              color: "text.secondary",
            },

            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .Mui-error .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
        >
          {placeholder && (
            <ToolTip content={tippyText} placement="right">
              <InputLabel
                onClick={onClickInfo}
                sx={{
                  display: "flex",
                  alignContent: "center",
                  width: "100%",
                  userSelect: "none",
                  "&.Mui-focused": {
                    color: "text.secondary",
                  },
                }}
              >
                {placeholder}
                {(infoButton || tippyText) && (
                  <Stack
                    direction={"row"}
                    alignContent={"center"}
                    marginLeft={"8px"}
                    flexWrap={"wrap"}
                  >
                    <InfoOutlined
                      sx={{
                        fill: "text.secondary",
                        color: "text.secondary",
                        cursor: "pointer",
                        width: "20px",
                        height: "20px",
                      }}
                    />
                  </Stack>
                )}
              </InputLabel>
            </ToolTip>
          )}
          <Select
            multiple
            value={selectedOptions}
            onChange={onChange}
            input={<OutlinedInput label={placeholder} />}
            renderValue={(selected) => {
              return selected.join(", ") + (suffix ? " " + suffix : "");
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: "300px",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    webkitAppearance: "none",
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    borderRadius: "2px",
                    backgroundColor: "text.icon",
                  },
                  "& .MuiDivider-root": {
                    marginY: 0.5,
                  },
                },
              },
            }}
            inputProps={{
              sx: {
                backgroundColor: "secondary.dark",
              },
            }}
          >
            {options.length > 3 && selectedOptions.length !== options.length && (
              <MenuItem value="select">
                <ListItemText primary={t("actions.selectall")} />
              </MenuItem>
            )}
            {options.length > 3 && selectedOptions.length > 0 && (
              <MenuItem value="deselect">
                <ListItemText primary={t("actions.deselectall")} />
              </MenuItem>
            )}
            {options.length > 3 && <Divider />}
            {options.map((option, idx) => (
              <MenuItem
                key={idx}
                value={option.value}
                disabled={option.disabled}
              >
                <Checkbox
                  sx={{
                    paddingLeft: "0",
                    paddingY: "0",
                    "&.MuiCheckbox-root": { color: "text.primary" },
                  }}
                  checked={selectedOptions.indexOf(option.value) > -1}
                />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      </Grid>
      {infoButton ? (
        <Grid
          item
          sx={{ display: "flex", marginLeft: "4px" }}
          onClick={onClickInfo}
        >
          <InfoOutlined
            sx={{
              fill: "text.secondary",
              cursor: "pointer",
              width: "20px",
              height: "20px",
            }}
          />
        </Grid>
      ) : null}
    </Grid>
  );
};

TagDropDown.defaultProps = {};

export default TagDropDown;
